import { Target, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createNotify } from '../../../redux/actions/notify';
import { getCompanyStructure } from '../../../redux/actions/structure';
import showNotification from '../../global/Notification/showNotification';
import './ReassessmentModal.scss';

const ReassessmentModal = ({ isOpen, onClose, execution, onSuccess }) => {
  const dispatch = useDispatch();
  const structure = useSelector(state => state.structure);
  const notify = useSelector(state => state.notify);

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const topicName = execution?.topic?.name || '';
  const topicSymbol = execution?.topic?.symbol;

  const availableUnits = Array.isArray(structure?.data) ? structure.data : [];

  const rolesForUnit = selectedUnit
    ? (availableUnits.find(u => u.name === selectedUnit)?.roles || [])
    : [];

  useEffect(() => {
    if (!isOpen) return;
    setSelectedUnit(null);
    setSelectedRoles([]);
    dispatch(getCompanyStructure());
  }, [isOpen, dispatch]);

  useEffect(() => {
    setSelectedRoles([...rolesForUnit]);
  }, [selectedUnit]);

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleConfirm = () => {
    const payload = {
      title: `Se avalie novamente no investimento: ${topicName}`,
      type: "REASSESSMENT_REQUEST",
      topic: { id: execution?.topic?.id, name: topicName, symbol: topicSymbol },
      units: selectedUnit ? [selectedUnit] : [],
      roles: selectedRoles
    };

    dispatch(createNotify(payload))
      .then(() => {
        showNotification('Notificação enviada com sucesso!', 5000);
        onSuccess();
        onClose();
      })
      .catch(() => showNotification('Houve um erro inesperado, tente mais tarde!', 5000));
  };

  if (!isOpen) return null;

  const canConfirm = !notify.isFetching && selectedUnit && selectedRoles.length > 0;

  return createPortal(
    <div className="rm-overlay">
      <div className="rm-backdrop" onClick={onClose} />

      <div className="rm-modal">

        <div className="rm-header">
          <h3 className="rm-title">Pedir Reavaliação</h3>
          <button className="rm-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="rm-competency">
          <span className="rm-symbol">{topicSymbol}</span>
          <span className="rm-competency-name">{topicName}</span>
        </div>

        <div className="rm-section">
          <span className="rm-section-label">Área</span>
          {structure.isFetching ? (
            <p className="rm-loading">Carregando áreas...</p>
          ) : availableUnits.length === 0 ? (
            <p className="rm-empty">Nenhuma área configurada para esta competência.</p>
          ) : (
            <div className="rm-chips">
              {availableUnits.map(unit => (
                <button
                  key={unit.name}
                  onClick={() => setSelectedUnit(selectedUnit === unit.name ? null : unit.name)}
                  className={`rm-chip${selectedUnit === unit.name ? ' rm-chip--active' : ''}`}
                >
                  {unit.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedUnit && (
          <div className="rm-section">
            <span className="rm-section-label">Cargos</span>
            {rolesForUnit.length === 0 ? (
              <p className="rm-empty">Nenhum cargo encontrado para esta área.</p>
            ) : (
              <div className="rm-chips">
                {rolesForUnit.map(role => (
                  <button
                    key={role}
                    onClick={() => toggleRole(role)}
                    className={`rm-chip${selectedRoles.includes(role) ? ' rm-chip--active' : ''}`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!selectedUnit && !structure.isFetching && availableUnits.length > 0 && (
          <p className="rm-hint">Selecione uma área para ver os cargos disponíveis.</p>
        )}

        <div className="rm-footer">
          <button className="rm-btn rm-btn--cancel" onClick={onClose}>
            Cancelar
          </button>
          <button className="rm-btn rm-btn--confirm" onClick={handleConfirm} disabled={!canConfirm}>
            {notify.isFetching ? 'Enviando...' : 'Pedir Reavaliação'}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default ReassessmentModal;
